class KoreaProxyCalculator extends HTMLElement {
  connectedCallback() {
    this.form = this.closest('form');
    this.exchangeRate = this.toNumber(this.dataset.exchangeRate, 170);
    this.baseShipping = this.toNumber(this.dataset.baseShipping, 0);
    this.shippingMap = this.parseMap(this.dataset.shippingMap || '');
    this.rounding = this.dataset.rounding || 'ceil';
    this.quantityProperty = this.querySelector('[data-proxy-quantity-property]');
    this.rateProperty = this.querySelector('[data-proxy-rate-property]');
    this.localKrwProperty = this.querySelector('[data-proxy-local-krw-property]');
    this.itemHkdProperty = this.querySelector('[data-proxy-item-hkd-property]');
    this.shippingProperty = this.querySelector('[data-proxy-shipping-property]');
    this.totalProperty = this.querySelector('[data-proxy-total-property]');
    this.paymentUnitsProperty = this.querySelector('[data-proxy-payment-units-property]');
    this.itemHkdOutput = this.querySelector('[data-proxy-item-hkd]');
    this.shippingOutput = this.querySelector('[data-proxy-shipping]');
    this.totalOutput = this.querySelector('[data-proxy-total]');

    this.installCartAddPatch();
    this.addEventListener('input', this.calculate);
    this.addEventListener('change', this.calculate);
    document.addEventListener('input', this.calculate, true);
    document.addEventListener('change', this.calculate, true);
    document.addEventListener('click', this.calculateSoon, true);
    this.form?.addEventListener('input', this.calculate);
    this.form?.addEventListener('change', this.calculate);
    this.form?.addEventListener('click', this.calculateSoon);
    this.form?.addEventListener('formdata', this.prepareFormData);
    this.positionQuantitySelector();
    this.quantityPlacementTimers = [window.setTimeout(this.positionQuantitySelector, 500), window.setTimeout(this.positionQuantitySelector, 1500)];
    this.calculate();
  }

  disconnectedCallback() {
    this.removeEventListener('input', this.calculate);
    this.removeEventListener('change', this.calculate);
    document.removeEventListener('input', this.calculate, true);
    document.removeEventListener('change', this.calculate, true);
    document.removeEventListener('click', this.calculateSoon, true);
    this.form?.removeEventListener('input', this.calculate);
    this.form?.removeEventListener('change', this.calculate);
    this.form?.removeEventListener('click', this.calculateSoon);
    this.form?.removeEventListener('formdata', this.prepareFormData);
    this.quantityPlacementTimers?.forEach((timer) => window.clearTimeout(timer));
  }

  calculateSoon = () => {
    window.requestAnimationFrame(this.calculate);
  };

  calculate = () => {
    const quantity = Math.max(1, this.toNumber(this.findQuantityInput()?.value, 1));
    this.updateQuantityButtonState(quantity);
    const localPriceInput = this.findLocalPriceInput();
    const localPrice = this.toNumber(localPriceInput?.value, 0);
    const itemHkd = this.exchangeRate > 0 ? (localPrice * quantity) / this.exchangeRate : 0;
    const roundedItemHkd = this.roundMoney(itemHkd);
    const shipping = this.roundMoney(this.findShippingFee() * quantity);
    const total = this.roundMoney(roundedItemHkd + shipping);

    this.writeText(this.itemHkdOutput, roundedItemHkd);
    this.writeText(this.shippingOutput, shipping);
    this.writeText(this.totalOutput, total);

    this.writeValue(this.quantityProperty, quantity);
    this.writeValue(this.rateProperty, `1 HKD : ${this.exchangeRate} KRW`);
    this.writeValue(this.localKrwProperty, localPrice);
    this.writeValue(this.itemHkdProperty, roundedItemHkd);
    this.writeValue(this.shippingProperty, shipping);
    this.writeValue(this.totalProperty, total);
    this.writeValue(this.paymentUnitsProperty, Math.max(1, Math.ceil(total)));
    this.currentTotal = total;
    this.customerQuantity = quantity;
  };

  findLocalPriceInput() {
    const root = this.closest('.shopify-section') || document;
    const inputs = Array.from(root.querySelectorAll('textarea, input'));
    return inputs.find((input) => input.placeholder?.trim() === '19900') || null;
  }

  findQuantityInput() {
    const root = this.closest('product-form-component') || this.form || document;
    return root.querySelector('input[name="quantity"]');
  }

  findQuantitySelector() {
    return this.findQuantityInput()?.closest('quantity-selector-component') || null;
  }

  positionQuantitySelector = () => {
    const root = this.closest('product-form-component') || this.closest('.shopify-section') || document;
    const quantityWrapper = root.querySelector('.quantity-selector-wrapper');
    const shippingRoot = Array.from(root.querySelectorAll('.po-option-root')).find((element) =>
      element.textContent?.includes('\u904B\u8CBB')
    );

    if (!quantityWrapper || !shippingRoot) return;

    const quantityInput = quantityWrapper.querySelector('input[name="quantity"]');
    const formId = this.form?.getAttribute('id');
    if (quantityInput && formId) quantityInput.setAttribute('form', formId);

    quantityWrapper.classList.add('quantity-selector-wrapper--proxy-shipping');
    if (shippingRoot.nextElementSibling !== quantityWrapper) {
      shippingRoot.insertAdjacentElement('afterend', quantityWrapper);
    }

    this.updateQuantityButtonState(this.toNumber(quantityInput?.value, 1));
  };

  updateQuantityButtonState(quantity) {
    const selector = this.findQuantitySelector();
    const quantityInput = selector?.querySelector('input[name="quantity"]');
    const minusButton = selector?.querySelector('.quantity-minus');
    if (!selector || !quantityInput || !minusButton) return;

    if ('serverDisabledMinus' in selector) selector.serverDisabledMinus = false;

    const min = Math.max(1, this.toNumber(quantityInput.min, 1));
    if (!quantityInput.disabled) {
      minusButton.disabled = quantity <= min;
    }
  }

  findShippingFee() {
    const selectedText = this.findEasifyShippingText();
    if (!selectedText) return this.baseShipping;

    const directMatch = this.shippingMap.get(selectedText);
    if (directMatch != null) return directMatch;

    for (const [label, fee] of this.shippingMap.entries()) {
      if (label && selectedText.includes(label)) return fee;
    }

    const numberMatch = selectedText.replace(/,/g, '').match(/(?:HK\$?\s*)?(\d+(?:\.\d+)?)/i);
    return numberMatch ? this.toNumber(numberMatch[1], this.baseShipping) : this.baseShipping;
  }

  findEasifyShippingText() {
    const root = this.closest('.shopify-section') || document;
    const optionRoots = Array.from(root.querySelectorAll('.po-option-root'));
    const shippingRoot = optionRoots.find((element) => element.textContent?.includes('\u904B\u8CBB'));
    const button = shippingRoot?.querySelector('.po-select-button');
    const text = button?.textContent?.trim().replace(/\s+/g, ' ');
    if (!text || /please select/i.test(text)) return '';
    return text;
  }

  prepareFormData = (event) => {
    this.calculate();

    const paymentUnits = Math.max(1, Math.ceil(this.currentTotal || 0));
    const customerQuantity = Math.max(1, this.customerQuantity || 1);

    event.formData.set('quantity', String(paymentUnits));
    event.formData.set('properties[Order quantity]', String(customerQuantity));
    event.formData.set('properties[_Payment units HK$1]', String(paymentUnits));
  };

  installCartAddPatch() {
    if (window.__koreaProxyCartAddPatchInstalled) return;

    const nativeFetch = window.fetch.bind(window);
    window.__koreaProxyCartAddPatchInstalled = true;
    window.fetch = (resource, options = {}) => {
      const url = typeof resource === 'string' ? resource : resource?.url || '';
      const body = options?.body;

      if (body instanceof FormData && url.includes('/cart/add') && body.has('properties[_Payment units HK$1]')) {
        const paymentUnits = Math.max(1, Math.ceil(this.toNumber(body.get('properties[_Payment units HK$1]'), 1)));
        body.set('quantity', String(paymentUnits));
      }

      return nativeFetch(resource, options);
    };
  }

  roundMoney(value) {
    if (!Number.isFinite(value)) return 0;
    if (this.rounding === 'none') return Math.round(value * 100) / 100;
    if (this.rounding === 'round') return Math.round(value);
    return Math.ceil(value);
  }

  toNumber(value, fallback) {
    const number = Number.parseFloat(String(value ?? '').replace(/,/g, ''));
    return Number.isFinite(number) ? number : fallback;
  }

  parseMap(value) {
    const map = new Map();
    String(value)
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .forEach((line) => {
        const separator = line.includes('=') ? '=' : ':';
        const [label, fee] = line.split(separator);
        if (label && fee != null) {
          map.set(label.trim(), this.toNumber(fee.trim(), 0));
        }
      });
    return map;
  }

  writeText(element, value) {
    if (element) element.textContent = `HK$${this.format(value)}`;
  }

  writeValue(element, value) {
    if (element) element.value = String(value);
  }

  format(value) {
    return Number(value).toLocaleString('en-HK', {
      minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
      maximumFractionDigits: 2,
    });
  }
}

if (!customElements.get('korea-proxy-calculator')) {
  customElements.define('korea-proxy-calculator', KoreaProxyCalculator);
}
